import type { RefCallBack } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CartLineCustomizationFile, Customization } from '@nucleus-commerce/api-client/admin';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { FileCustomizationPicker } from './file-customization-picker';

export interface CustomizationFieldProps {
  def: Customization;
  value: string | boolean;
  onChange: (value: string | boolean) => void;
  onBlur: () => void;
  name: string;
  inputRef: RefCallBack;
  invalid: boolean;
  /** The file already on the line, for a file customization being edited. */
  kept?: CartLineCustomizationFile | null;
}

/** One customization input, rendered inside a FormField of `customizations.<key>`. */
export function CustomizationField({
  def,
  value,
  onChange,
  onBlur,
  name,
  inputRef,
  invalid,
  kept = null,
}: CustomizationFieldProps) {
  const { t } = useTranslation('draftOrders');
  const label = (
    <FormLabel>
      {def.label}
      {def.isRequired ? <span aria-hidden> *</span> : null}
    </FormLabel>
  );
  return (
    <FormItem>
      {def.inputType === 'boolean' ? (
        <div className="flex items-center justify-between gap-3 rounded-md border p-3">
          <FormLabel>{def.label}</FormLabel>
          <FormControl>
            <Switch checked={value === true} onCheckedChange={(checked) => onChange(checked)} />
          </FormControl>
        </div>
      ) : (
        label
      )}
      {def.inputType === 'select' ? (
        <Select value={String(value)} onValueChange={onChange}>
          <FormControl>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('picker.chooseOption')} />
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            {(def.options ?? []).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : def.inputType === 'text' || def.inputType === 'number' ? (
        <FormControl>
          <Input
            type={def.inputType === 'number' ? 'number' : 'text'}
            autoComplete="off"
            maxLength={def.maxLength ?? undefined}
            value={String(value)}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            name={name}
            ref={inputRef}
          />
        </FormControl>
      ) : def.inputType === 'file' ? (
        <FileCustomizationPicker
          def={def}
          value={String(value)}
          onChange={onChange}
          kept={kept}
          invalid={invalid}
        />
      ) : null}
      {def.helpText ? <FormDescription>{def.helpText}</FormDescription> : null}
      <FormMessage />
    </FormItem>
  );
}
